/* globals ca */
import { has } from 'lodash';

/**
 * Before / After
 */

import { BeforeAfter, Media } from 'ca-api-client';

const initialState = () => ({
    beforeSelected: [],
    afterSelected: [],
});

export const baState = {
    beforeAfter: {
        ...initialState(),
    },
};

// mutations
export const baMutations = {
    /**
     * Sets `before_after` metadata
     * @param {object} meta
     *
     * Should be in format:
     * {before: {media_id: <media_id>}, after: {media_id: <media_id>}}
     */
    setBeforeAfterMeta(state, meta) {
        const metadata = state.mediaview.currentItem.metadata;
        if (has(metadata, 'before_after')) {
            metadata.before_after = meta;
        } else {
            Object.assign({}, metadata, { before_after: meta });
        }
    },

    /**
     * Sets one side of a `before_after` media
     * @param {object} payload
     *
     * Should be in format:
     * {side: <before|after>, {meta: {media_id: <media_id>, label: <label>}}}
     */
    setBeforeAfterMetaSide(state, payload) {
        let metadata = state.mediaview.currentItem.metadata;
        if (has(metadata, 'before_after')) {
            //metadata.before_after[payload.side] = payload.meta;
            Object.assign({}, metadata.before_after, { [payload.side]: payload.meta });
        } else {
            Object.assign({}, metadata, { before_after: { [payload.side]: payload.meta } });
        }
    },

    /**
     * Sets the label for a `before_after` side
     * @param {object} payload
     *
     * Should be in format:
     * {side: <before|after>, label: <label>}
     */
    setMediaEditBeforeAfterLabel(state, payload) {
        const side = payload.side;
        const metadata = state.mediaview.currentItem.metadata;
        if (has(metadata, 'before_after') && has(metadata.before_after, side)) {
            metadata.before_after[side].label = payload.label;
        } else {
            metadata.before_after[side] = { label: payload.label };
        }
    },

    /**
     * Sets the media for a `before_after` side
     * @param {object} payload
     *
     * Should be in format:
     * {side: <before|after>, id: <media_id>}
     */
    setMediaEditBeforeAfterMedia(state, payload) {
        const side = payload.side;
        const metadata = state.mediaview.currentItem.metadata;
        if (has(metadata, 'before_after') && has(metadata.before_after, side)) {
            metadata.before_after[side].media_id = payload.id;
        } else {
            metadata.before_after[side] = { media_id: payload.id };
        }
    },

    /**
     * Sets state of sidebar
     * @param {boolena} open
     * @returns {boolean}
     */
    setBeforeAfterSidebarOpen(state, open) {
        state.mediaedit.sidebarOpen = open;
    },

    /**
     * Sets before selected items array
     * @param {array} items - An array of Media objects
     */
    setBeforeSelectedItems(state, items) {
        state.beforeAfter.beforeSelected = items;
    },

    /**
     * Sets before selected items array
     * @param {array} items - An array of Media objects
     */
    setAfterSelectedItems(state, items) {
        state.beforeAfter.afterSelected = items;
    },
};

// getters
export const baGetters = {
    /**
     * Gets media edit current items type
     * @returns {string} - Media.type
     */
    getMediaEditCurrentItemType(state) {
        return state.mediaview.currentItem.type;
    },

    /**
     * Gets `before_after` before label
     * @returns {string}
     */
    getMediaEditBeforeAfterBeforeLabel(state) {
        let label = null;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, 'before') &&
            has(metadata.before_after.before, 'label')
        ) {
            label = metadata.before_after.before.label;
        }
        return label;
    },

    /**
     * Gets `before_after` after label
     * @returns {string}
     */
    getMediaEditBeforeAfterAfterLabel(state) {
        let label = null;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, 'before') &&
            has(metadata.before_after.after, 'label')
        ) {
            label = metadata.before_after.after.label;
        }
        return label;
    },

    /**
     * Gets `before_after` before media_id
     * @returns {string}
     */
    getMediaEditBeforeAfterBeforeMedia(state) {
        let media_id = false;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, 'before') &&
            has(metadata.before_after.before, 'media_id')
        ) {
            media_id = metadata.before_after.before.media_id;
        }
        return media_id;
    },

    /**
     * Gets `before_after` after media_id
     * @returns {string}
     */
    getMediaEditBeforeAfterAfterMedia(state) {
        let media_id = false;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, 'after') &&
            has(metadata.before_after.after, 'media_id')
        ) {
            media_id = metadata.before_after.after.media_id;
        }
        return media_id;
    },

    /**
     * Gets `before_after` sides label
     * @param {string} side - before|after
     * @returns {string}
     */
    getMediaEditBeforeAfterLabel: (state) => (side) => {
        let label = null;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, side) &&
            has(metadata.before_after[side], 'label')
        ) {
            label = metadata.before_after[side].label;
        }
        return label;
    },

    /**
     * Gets `before_after` sides media_id
     * @param {string} side - before|after
     * @returns {string}
     */
    getMediaEditBeforeAfterMedia: (state) => (side) => {
        let media_id = false;
        const metadata = state.mediaview.currentItem.metadata;
        if (
            has(metadata, 'before_after') &&
            has(metadata.before_after, side) &&
            has(metadata.before_after[side], 'media_id')
        ) {
            media_id = metadata.before_after[side].media_id;
        }
        return media_id;
    },

    /**
     * Gets state of sidebar
     * @returns {boolean}
     */
    getBeforeAfterSidebarOpen(state) {
        return state.mediaedit.sidebarOpen;
    },

    /**
     * Gets before selected items array
     * @returns {array}
     */
    getBeforeSelectedItems(state) {
        return state.beforeAfter.beforeSelected;
    },

    /**
     * Gets after selected items array
     * @returns {array} i
     */
    getAfterSelectedItems(state) {
        return state.beforeAfter.afterSelected;
    },
};

// actions
export const baActions = {
    /**
     * Sets `before_after` sides metadata
     * @param {object} payload - {side: <before|after>, item: <Media>}
     * @returns {object} Promise
     */
    setMediaEditBeforeAfterSide({ commit }, payload) {
        commit('setBeforeAfterMetaSide', {
            side: payload.side,
            meta: payload.item.metadata.before_after[payload.side],
        });
        //state.mediaedit.currentItem.metadata.before_after[payload.side] = payload.item.metadata.before_after[payload.side];
    },

    /**
     * Fetches a `before_after` from the api
     * @param {string} id - The media items id
     * @returns {object} Promise
     */
    async fetchBeforeAfter({ commit }, id) {
        const ba = await BeforeAfter.find(id);
        //commit('setMediaEditCurrentItem', ba.data);
        return ba;
    },

    /**
     * Creates a new `before_after`
     * @returns {object} Promise
     */
    async createBeforeAfter({ commit, dispatch }, payload) {
        // example payload
        // {
        //     type: 'before_after',
        //     title: 'New Before / After',
        //     in_review: false,
        //     public: false,
        //     client_id: ca.client.uuid,
        //     project_id: ca.project.uuid,
        //     metadata: {
        //         before_after: {
        //             title: null,
        //             description: null,
        //             direction: 'horizontal',
        //             before: {
        //                 label: 'Before',
        //                 media_id: null,
        //             },
        //             after: {
        //                 label: 'After',
        //                 media_id: null,
        //             },
        //         },
        //     },
        // }

        const ba = new Media(payload);

        const newBa = await ba.save();
        dispatch('prependGalleryMediaItem', new Media(newBa.data)).then(() => {
            commit('setMediaViewCurrentItem', new Media(newBa.data));
        });

        return newBa;
    },

    /**
     * Updates/saves the current `before_after` in the api
     * @returns {object} Promise
     */
    async updateBeforeAfter({ state, getters }) {
        const item = getters.getMediaViewCurrentItem;
        const ba = await BeforeAfter.$find(item.id);

        ba.metadata = item.metadata;
        //ba.in_review = false;
        if (!ba.description) delete ba.description;

        //ba = Object.assign(ba, state.mediaedit.currentItem);

        const updatedBa = await ba.save();
        return updatedBa;
    },

    /**
     * Fetches the media for a side of the current `before_after`
     * @param {string} side = before|after
     * @returns {object} Promise
     */
    async fetchBeforeAfterMedia({ state }, side) {
        let bm = false;
        const id = state.mediaedit.currentItem.metadata.before_after[side].media_id;
        if (id) {
            bm = await BeforeAfter.find(id);
        }
        return bm;
    },

    /**
     * Updates the label for a side of the current `before_after`
     * @param {object} payload - {side: <before|after>, label: <label>}
     * @returns {object} Promise
     */
    async updateBeforeAfterLabel({ commit, dispatch }, payload) {
        commit('setMediaEditBeforeAfterLabel', payload);
        return await dispatch('updateBeforeAfter');
    },

    /**
     * Adds an item to before selected
     * @param {object} item - Media
     */
    addBeforeSelectItem({ commit, getters, state }, item) {
        const selected = state.beforeAfter.beforeSelected.map(i => i.id);
        const exists = selected.includes(item.id);

        if (item.checked && !exists) {
            commit('setBeforeSelectedItems', [item]);
        } else {
            commit('setBeforeSelectedItems', []);
        }
    },

    /**
     * Adds an item to after selected
     * @param {object} item - Media
     */
    addAfterSelectItem({ commit, getters, state }, item) {
        const selected = state.beforeAfter.afterSelected.map(i => i.id);
        const exists = selected.includes(item.id);

        if (item.checked && !exists) {
            commit('setAfterSelectedItems', [item]);
        } else {
            commit('setAfterSelectedItems', []);
        }
    },

    /**
     * Resets before after to original state
     * @see initialState()
     * @return {object} Promise
     */
    resetBeforeAfterState({ state }) {
        state.beforeAfter = Object.assign(
            {},
            {
                ...initialState(),
            }
        );
    },
};
